// Admin "Marketplace Apps" — OAuth app credential entry for each marketplace.
//
// Volkan enters each marketplace's OAuth *app* credentials (Client ID + Secret)
// here instead of env vars, and can return to inspect them masked. The Etsy +
// Shopify OAuth flows resolve these DB-first (env fallback). Real-backend surface
// (/admin/marketplace-credentials) — secrets are Fernet-encrypted server-side and
// NEVER returned; the read shape exposes a last-4 hint + presence flag only.
// Admin Ring 3 (marketplace_credential.read/.write).
//
// Generic over providers: add a marketplace by appending one PROVIDERS entry
// (+ widening the backend CHECK). Shopify + Etsy ship now.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, Plug, ShieldCheck, ExternalLink } from 'lucide-react'
import { apiUrl, USE_REAL_API } from '../../lib/api'
import { toast } from '../../lib/toast'
import {
  ConfiguredBadge,
  LockedCredentialNotice,
} from '../../components/credential-lock'
import { useEditGate } from '../../components/credential-lock.lib'
import { useT } from '../../i18n'
import { marketplaceAppsI18n } from '../../i18n/admin/marketplace-apps'

type Provider = 'shopify' | 'etsy' | 'amazon' | 'printify' | 'jds' | 'hedi2' | 'gelato' | 'pinterest'

type ProviderStatus = {
  provider: string
  configured: boolean
  source: 'db' | 'env' | 'none'
}

type CredentialRead = {
  id: string
  provider: Provider
  display_name: string
  client_id: string | null
  client_secret_hint: string | null
  has_client_secret: boolean
  livemode: boolean
  active: boolean
  last_validated_at: string | null
  created_at: string
  updated_at: string
}

type ProviderMeta = {
  id: Provider
  label: string
  blurb: string
  clientIdLabel: string
  clientIdHint: string
  secretLabel: string
  docsUrl: string
  docsHint: string
  // Amazon needs a SECOND non-secret id (the LWA client id) alongside the
  // SP-API application_id that lives in clientId. Absent → single-id provider.
  lwaClientIdLabel?: string
  lwaClientIdHint?: string
  // Token-only suppliers (Printify/JDS) have no client id at all → hide the field.
  noClientId?: boolean
  // Hedi2 signs its webhook pushes; the HMAC key is a second secret stored in
  // the encrypted blob. Absent → no webhook-secret field.
  webhookSecretLabel?: string
  webhookSecretHint?: string
}

// Text fields (blurb, field labels, hints) come from marketplaceAppsI18n.
type ProviderBase = Pick<ProviderMeta, 'id' | 'label' | 'docsUrl' | 'docsHint' | 'noClientId'>

const PROVIDERS: ProviderBase[] = [
  {
    id: 'shopify',
    label: 'Shopify',
    docsUrl: 'https://partners.shopify.com',
    docsHint: 'Shopify Partners → Apps → Client credentials',
  },
  {
    id: 'etsy',
    label: 'Etsy',
    docsUrl: 'https://www.etsy.com/developers/your-apps',
    docsHint: 'Etsy Developers → Your Apps → Keystring + Shared secret',
  },
  {
    id: 'amazon',
    label: 'Amazon',
    docsUrl: 'https://sellercentral.amazon.com/sw/AccountInfo/DeveloperCentral',
    docsHint: 'Solution Provider Portal → your app → LWA credentials',
  },
  {
    id: 'printify',
    label: 'Printify',
    noClientId: true,
    docsUrl: 'https://printify.com/app/account/api',
    docsHint: 'Printify → My Profile → Connections → Generate token',
  },
  {
    id: 'jds',
    label: 'JDS Industries',
    noClientId: true,
    docsUrl: 'https://shop.jdsindustries.com/?view=apiRequest',
    docsHint: 'JDS shop account → API Information',
  },
  {
    id: 'hedi2',
    label: 'Hedi2',
    docsUrl: 'https://claude.ai/code/artifact/c9932509-5b34-4509-b45b-3a488cc7ac42',
    docsHint: 'Hedi2 × Pexify integration brief (key exchange)',
  },
  {
    id: 'gelato',
    label: 'Gelato',
    noClientId: true,
    docsUrl: 'https://dashboard.gelato.com/docs/',
    docsHint: 'Gelato Dashboard → API Portal → API Keys',
  },
  {
    id: 'pinterest',
    label: 'Pinterest',
    docsUrl: 'https://developers.pinterest.com/apps/',
    docsHint: 'Pinterest Developers → My apps → Manage → App ID + App secret key',
  },
]

const INPUT_CLS =
  'w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-[13px] font-mono text-slate-900 outline-none focus:border-indigo-300 focus:shadow-[0_0_0_3px_rgb(238_242_255)]'

type ListResponse = { items: CredentialRead[]; statuses: ProviderStatus[] }

async function fetchCredentials(): Promise<ListResponse> {
  const res = await fetch(apiUrl('/admin/marketplace-credentials'))
  if (!res.ok) throw new Error(`fetch marketplace credentials ${res.status}`)
  return (await res.json()) as ListResponse
}

export function MarketplaceApps() {
  const t = useT(marketplaceAppsI18n)
  const qc = useQueryClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-marketplace-credentials'],
    queryFn: fetchCredentials,
  })

  const byProvider = new Map((data?.items ?? []).map((r) => [r.provider, r]))
  const byStatus = new Map((data?.statuses ?? []).map((s) => [s.provider, s]))

  return (
    <div className="space-y-4">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-slate-900">
          <Plug size={22} className="text-indigo-600" aria-hidden="true" />
          {t.title}
        </h1>
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500">
          <ShieldCheck size={12} className="text-emerald-600" aria-hidden="true" />
          {t.encryptedNote}
        </p>
      </header>

      {!USE_REAL_API && (
        <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800 ring-1 ring-amber-200">
          {t.simulationNotice}
        </div>
      )}

      {isLoading ? (
        <div className="rounded-lg bg-white p-8 text-center text-sm text-slate-500 ring-1 ring-slate-200">
          {t.loading}
        </div>
      ) : error ? (
        <div className="rounded-lg bg-white p-8 text-center text-sm text-rose-700 ring-1 ring-slate-200">
          {t.loadError}
        </div>
      ) : (
        <div className="space-y-4">
          {PROVIDERS.map((base) => {
            const meta: ProviderMeta = { ...base, ...t.providers[base.id] }
            const status = byStatus.get(meta.id)
            return (
              <ProviderCard
                key={meta.id}
                meta={meta}
                current={byProvider.get(meta.id) ?? null}
                configured={Boolean(status?.configured)}
                sourceLabel={status && status.source !== 'none' ? t.sourceLabels[status.source] : null}
                onSaved={() =>
                  qc.invalidateQueries({ queryKey: ['admin-marketplace-credentials'] })
                }
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

function ProviderCard({
  meta,
  current,
  configured,
  sourceLabel,
  onSaved,
}: {
  meta: ProviderMeta
  current: CredentialRead | null
  configured: boolean
  sourceLabel: string | null
  onSaved: () => void
}) {
  const t = useT(marketplaceAppsI18n)
  const gate = useEditGate(configured)
  const [clientId, setClientId] = useState('')
  const [lwaClientId, setLwaClientId] = useState('')
  const [webhookSecret, setWebhookSecret] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [livemode, setLivemode] = useState(false)
  const [active, setActive] = useState(true)

  // Prefill the non-secret + presence fields when a row already exists. The
  // secret input stays blank (write-only) — a rotation always re-enters it. The
  // LWA client id lives in the encrypted blob (not echoed on read), so it also
  // re-enters on rotation.
  const [prefilled, setPrefilled] = useState<string | null>(null)
  if (current && prefilled !== current.id) {
    setPrefilled(current.id)
    setClientId(current.client_id ?? '')
    setLwaClientId('')
    setLivemode(current.livemode)
    setActive(current.active)
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(apiUrl(`/admin/marketplace-credentials/${meta.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          display_name: meta.label,
          client_id: meta.noClientId ? null : clientId.trim() || null,
          lwa_client_id: meta.lwaClientIdLabel ? lwaClientId.trim() || null : null,
          webhook_secret: meta.webhookSecretLabel ? webhookSecret.trim() || null : null,
          client_secret: clientSecret.trim(),
          livemode,
          active,
        }),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { detail?: { message?: string } }
          | null
        throw new Error(body?.detail?.message ?? `save failed (${res.status})`)
      }
      return (await res.json()) as CredentialRead
    },
    onSuccess: () => {
      toast.success(t.savedToast(meta.label), {
        description: t.savedToastDescription,
        audience: 'admin',
      })
      setClientSecret('')
      setLwaClientId('')
      setWebhookSecret('')
      gate.lock()
      onSaved()
    },
    onError: (e: unknown) => {
      toast.error(t.saveErrorToast, {
        description: e instanceof Error ? e.message : t.unknownError,
        audience: 'admin',
      })
    },
  })

  // Amazon needs all three ids to build a working consent + token exchange, so
  // require the application id + LWA client id too when this provider has them.
  const amazonIdsOk =
    !meta.lwaClientIdLabel || (clientId.trim().length > 0 && lwaClientId.trim().length > 0)
  const canSave =
    USE_REAL_API && clientSecret.trim().length > 0 && amazonIdsOk && !mutation.isPending

  return (
    <div className="overflow-hidden rounded-lg bg-white ring-1 ring-slate-200">
      <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-indigo-50 text-indigo-700">
            <Plug size={18} aria-hidden="true" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">{meta.label}</div>
            <div className="text-[11px] text-slate-500">{meta.blurb}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {current && <ModeBadge livemode={current.livemode ?? livemode} />}
          <ConfiguredBadge configured={configured} sourceLabel={sourceLabel} />
        </div>
      </div>

      {!gate.editing ? (
        <div className="space-y-3 px-5 py-5">
          {current?.client_id && (
            <MaskedField icon={Plug} label={meta.clientIdLabel} value={current.client_id} ok mono />
          )}
          <LockedCredentialNotice
            hint={current?.client_secret_hint}
            onEdit={gate.requestEdit}
            label={t.credentialsLabel(meta.label)}
          />
        </div>
      ) : (
        <div className="space-y-4 px-5 py-5">
          {!meta.noClientId && (
            <Field label={meta.clientIdLabel} hint={meta.clientIdHint}>
              <input
                type="text"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder={meta.clientIdLabel}
                className={INPUT_CLS}
              />
            </Field>
          )}

          {meta.lwaClientIdLabel && (
            <Field label={meta.lwaClientIdLabel} hint={meta.lwaClientIdHint}>
              <input
                type="text"
                value={lwaClientId}
                onChange={(e) => setLwaClientId(e.target.value)}
                placeholder={meta.lwaClientIdLabel}
                className={INPUT_CLS}
              />
            </Field>
          )}

          {meta.webhookSecretLabel && (
            <Field label={meta.webhookSecretLabel} hint={meta.webhookSecretHint}>
              <input
                type="password"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder="••••••••"
                autoComplete="off"
                className={INPUT_CLS}
              />
            </Field>
          )}

          <Field
            label={
              current?.has_client_secret ? t.rotateSuffix(meta.secretLabel) : meta.secretLabel
            }
            hint={t.secretHint}
            required
          >
            <input
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder="••••••••"
              autoComplete="off"
              className={INPUT_CLS}
            />
          </Field>

          <div className="flex flex-wrap items-center gap-5 pt-1">
            <Toggle label={t.liveMode} checked={livemode} onChange={setLivemode} />
            <Toggle label={t.active} checked={active} onChange={setActive} />
          </div>

          <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-4">
            <a
              href={meta.docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700"
            >
              <ExternalLink size={11} aria-hidden="true" />
              {t.whereToFind(meta.docsHint)}
            </a>
            <div className="flex items-center gap-2">
              {configured && (
                <button
                  type="button"
                  onClick={gate.lock}
                  className="rounded-md border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100"
                >
                  {t.cancel}
                </button>
              )}
              <button
                type="button"
                disabled={!canSave}
                onClick={() => mutation.mutate()}
                className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-xs font-medium text-white transition-all hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {mutation.isPending ? t.saving : current ? t.rotateCredentials : t.saveCredentials}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ModeBadge({ livemode }: { livemode: boolean }) {
  const t = useT(marketplaceAppsI18n)
  return (
    <span
      className={
        'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold ring-1 ' +
        (livemode
          ? 'bg-rose-50 text-rose-700 ring-rose-200'
          : 'bg-slate-100 text-slate-600 ring-slate-200')
      }
    >
      {livemode ? t.modeLive : t.modeTest}
    </span>
  )
}

function MaskedField({
  icon: Icon,
  label,
  value,
  ok,
  mono,
}: {
  icon: typeof KeyRound
  label: string
  value: string
  ok: boolean
  mono?: boolean
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        <Icon size={11} aria-hidden="true" />
        {label}
      </div>
      <div
        className={
          'mt-1 truncate text-xs ' +
          (mono ? 'font-mono ' : '') +
          (ok ? 'text-slate-800' : 'text-slate-400')
        }
        title={value}
      >
        {value}
      </div>
    </div>
  )
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string
  hint?: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <div className="mb-1 flex items-baseline gap-1.5">
        <span className="text-xs font-medium text-slate-700">{label}</span>
        {required && <span className="text-rose-500">*</span>}
        {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
      </div>
      {children}
    </label>
  )
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="inline-flex items-center gap-2 text-xs font-medium text-slate-700"
    >
      <span
        className={
          'relative h-5 w-9 rounded-full transition-colors ' +
          (checked ? 'bg-indigo-600' : 'bg-slate-300')
        }
      >
        <span
          className={
            'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ' +
            (checked ? 'left-4' : 'left-0.5')
          }
        />
      </span>
      {label}
    </button>
  )
}
